import { zodResolver } from '@hookform/resolvers/zod';
import { EnvironmentSchema, type UpdateActivityProfileInput } from '@onflow/shared';
import { useForm, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useOnboardingProfile, useUpdateActivity } from '../../hooks/useOnboarding.js';
import { toast } from '../../lib/toast.js';

const ENVIRONMENT_OPTIONS: ReadonlyArray<{
  value: z.infer<typeof EnvironmentSchema>;
  label: string;
}> = [
  { value: 'gym', label: 'Gym' },
  { value: 'home', label: 'Home' },
  { value: 'outdoor', label: 'Outdoor' },
];

// Form-side schema. One coarse daily-movement choice gives the deterministic
// TDEE calculator a lifestyle baseline without restoring the old hour-by-hour
// questionnaire. Sport intensity remains defaulted at submit.
const SportFormEntrySchema = z.object({
  name: z.string().min(1).max(80),
  sessions_per_week: z
    .number({ invalid_type_error: 'Enter sessions' })
    .int()
    .min(1)
    .max(7)
    .or(z.nan().transform(() => 0)),
  hours_per_session: z
    .number({ invalid_type_error: 'Enter hours' })
    .min(0.25)
    .max(12)
    .or(z.nan().transform(() => 0)),
});

const ActivityFormSchema = z
  .object({
    environments: z.array(EnvironmentSchema).default([]),
    sports: z.array(SportFormEntrySchema).default([]),
    daily_movement: z.enum(['mostly_seated', 'mixed', 'mostly_on_feet']),
    trainingSelected: z.boolean().default(true),
  })
  .superRefine((value, ctx) => {
    if (value.trainingSelected && value.environments.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['environments'],
        message: 'Pick at least one training environment',
      });
    }
  });

type ActivityFormValues = z.infer<typeof ActivityFormSchema>;

export function ActivityScreen() {
  const navigate = useNavigate();
  const updateActivity = useUpdateActivity();
  const { data: profile } = useOnboardingProfile();
  const trainingSelected = profile?.trackingModes?.includes('training') ?? true;
  const stored = profile?.onboardingData as
    | {
        sports?: ActivityFormValues['sports'];
        lifestyleBaseline?: {
          daily_movement?: ActivityFormValues['daily_movement'];
        };
      }
    | undefined;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ActivityFormValues>({
    resolver: zodResolver(ActivityFormSchema),
    defaultValues: {
      environments: profile?.environments ?? [],
      sports: stored?.sports ?? [],
      daily_movement: stored?.lifestyleBaseline?.daily_movement ?? 'mixed',
      trainingSelected,
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'sports' });

  function buildPayload(values: ActivityFormValues): UpdateActivityProfileInput {
    // Intensity defaulted: omitted from UI for friction reduction. AI prompts
    // adapt to whatever signal is provided.
    const sports = values.sports.map((s) => ({
      name: s.name,
      sessions_per_week: s.sessions_per_week,
      hours_per_session: s.hours_per_session,
      intensity: 'moderate' as const,
    }));

    return {
      environments: values.environments,
      sports,
      lifestyleBaseline: {
        daily_movement: values.daily_movement,
      },
    };
  }

  async function onValidSubmit(values: ActivityFormValues) {
    try {
      await updateActivity.mutateAsync(buildPayload(values));
      navigate('/onboarding/training');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save activity');
    }
  }

  const saving = updateActivity.isPending;

  return (
    <section className="onboarding-screen">
      <form
        className="onboarding-card onboarding-form"
        onSubmit={handleSubmit(onValidSubmit)}
        noValidate
      >
        <h2 className="onboarding-title">Your activity baseline</h2>
        <p className="onboarding-subtitle">Where you train and what you already do.</p>

        {trainingSelected && (
          <fieldset className="form-group checkbox-group" aria-invalid={!!errors.environments}>
            <legend className="form-legend">Where do you train?</legend>
            {ENVIRONMENT_OPTIONS.map((opt) => (
              <label key={opt.value} className="checkbox-option">
                <input type="checkbox" value={opt.value} {...register('environments')} />
                <span>{opt.label}</span>
              </label>
            ))}
            {errors.environments && (
              <span role="alert" className="form-error">
                {errors.environments.message ?? 'Pick at least one environment'}
              </span>
            )}
          </fieldset>
        )}

        <div className="form-group">
          <label htmlFor="lifestyle-movement">Outside planned exercise, most days are</label>
          <select id="lifestyle-movement" {...register('daily_movement')}>
            <option value="mostly_seated">Mostly seated</option>
            <option value="mixed">A mix of sitting and moving</option>
            <option value="mostly_on_feet">Mostly on my feet</option>
          </select>
          <span className="form-hint">Used only by the calorie calculator.</span>
        </div>

        <div className="form-group">
          <span className="form-legend">Sports & activities</span>
          <p className="form-sublabel-tight">
            Add anything you currently do regularly. Skip if none.
          </p>
          {fields.length > 0 && (
            <div className="field-array">
              {fields.map((field, idx) => {
                const nameId = `sport-${idx}-name`;
                const sessionsId = `sport-${idx}-sessions`;
                const hoursId = `sport-${idx}-hours`;
                return (
                  <div key={field.id} className="field-array-row">
                    <div className="field-array-cell">
                      <label htmlFor={nameId} className="field-array-row-label">
                        Sport
                      </label>
                      <input
                        id={nameId}
                        type="text"
                        placeholder="e.g., tennis"
                        maxLength={80}
                        aria-label={`Sport ${idx + 1} name`}
                        {...register(`sports.${idx}.name` as const)}
                      />
                    </div>
                    <div className="field-array-cell">
                      <label htmlFor={sessionsId} className="field-array-row-label">
                        Sessions/week
                      </label>
                      <input
                        id={sessionsId}
                        type="number"
                        min={1}
                        max={7}
                        step={1}
                        inputMode="numeric"
                        placeholder="3"
                        aria-label={`Sport ${idx + 1} sessions per week`}
                        {...register(`sports.${idx}.sessions_per_week` as const, {
                          valueAsNumber: true,
                        })}
                      />
                    </div>
                    <div className="field-array-cell">
                      <label htmlFor={hoursId} className="field-array-row-label">
                        Hours/session
                      </label>
                      <input
                        id={hoursId}
                        type="number"
                        min={0.25}
                        max={12}
                        step={0.25}
                        inputMode="decimal"
                        placeholder="1"
                        aria-label={`Sport ${idx + 1} hours per session`}
                        {...register(`sports.${idx}.hours_per_session` as const, {
                          valueAsNumber: true,
                        })}
                      />
                    </div>
                    <button
                      type="button"
                      className="field-array-remove"
                      aria-label={`Remove sport ${idx + 1}`}
                      onClick={() => remove(idx)}
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <button
            type="button"
            className="field-array-add"
            onClick={() => append({ name: '', sessions_per_week: 0, hours_per_session: 0 })}
          >
            + Add sport
          </button>
        </div>

        <div className="onboarding-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/onboarding/body-stats')}
          >
            Back
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : 'Next'}
          </button>
        </div>
      </form>
    </section>
  );
}
